/**
 * Admin Grow Section - Dispatch Services with Package Selection, Load Board & Client Profiles
 * Flow: Select Package → View Load Board → Click Carrier to View Profile
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/admin/AdminLayout';
import ClientProfileModal from '../../../components/admin/ClientProfileModal';
import { 
  TrendingUp, Truck, Users, Star, DollarSign, MapPin,
  ArrowUpRight, ArrowLeft, Activity, Target, Award,
  Loader2, BarChart3, Phone, Clock, Zap, Crown,
  Package, Navigation, CheckCircle, AlertCircle, Circle,
  Mail, Building, User, Calendar, FileText, X, CreditCard,
  Shield, Hash, Percent
} from 'lucide-react';

const AdminGrowSection = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [activeTab, setActiveTab] = useState('loadboard');
  const [selectedLoad, setSelectedLoad] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  
  // Client/Carrier profiles database
  const clientProfiles = {
    'Solo Express LLC': {
      id: 'CLT-001',
      company: 'Solo Express LLC',
      package: 'Bronze',
      contact: { name: 'James Wilson', title: 'Owner/Operator', email: 'james@soloexpress.com', phone: '(214) 555-0123' },
      address: '1234 Highway Dr, Dallas, TX 75201',
      dotNumber: '3847291',
      mcNumber: '1029384',
      fleet: { trucks: 1, trailers: 1, drivers: 1 },
      insurance: { expiry: '2026-08-15', provider: 'Progressive Commercial', coverage: '$1,000,000' },
      joined: '2025-11-15',
      stats: { totalLoads: 89, onTimeDelivery: 94, avgRating: 4.7, totalRevenue: 187500, avgRatePerMile: 2.45 },
      loadHistory: [
        { id: 'BRZ-001', route: 'Dallas, TX → Atlanta, GA', date: '2026-03-18', rate: 3200, status: 'in_transit' },
        { id: 'BRZ-012', route: 'Houston, TX → Memphis, TN', date: '2026-03-10', rate: 1850, status: 'delivered' },
        { id: 'BRZ-008', route: 'Phoenix, AZ → Dallas, TX', date: '2026-03-05', rate: 2100, status: 'delivered' }
      ],
      notes: 'Reliable owner-operator. Prefers regional Southeast lanes. Always on time.'
    },
    'Highway Runner': {
      id: 'CLT-002',
      company: 'Highway Runner',
      package: 'Bronze',
      contact: { name: 'Mike Chen', title: 'Owner', email: 'mike@highwayrunner.com', phone: '(713) 555-0456' },
      address: '567 Transport Blvd, Houston, TX 77001',
      dotNumber: '3921847',
      mcNumber: '1183726',
      fleet: { trucks: 1, trailers: 2, drivers: 1 },
      insurance: { expiry: '2026-06-20', provider: 'Northland Insurance', coverage: '$1,000,000' },
      joined: '2025-12-01',
      stats: { totalLoads: 67, onTimeDelivery: 91, avgRating: 4.5, totalRevenue: 142000, avgRatePerMile: 2.38 },
      loadHistory: [
        { id: 'BRZ-002', route: 'Houston, TX → Phoenix, AZ', date: '2026-03-21', rate: 2850, status: 'pending' },
        { id: 'BRZ-015', route: 'Dallas, TX → Denver, CO', date: '2026-03-12', rate: 2200, status: 'delivered' }
      ],
      notes: 'Good for long-haul Southwest routes. Expanding to add second truck.'
    },
    'Mile High Trucking': {
      id: 'CLT-003',
      company: 'Mile High Trucking',
      package: 'Bronze',
      contact: { name: 'Sarah Johnson', title: 'Owner/Operator', email: 'sarah@milehigh.com', phone: '(303) 555-0789' },
      address: '890 Mountain Ave, Denver, CO 80201',
      dotNumber: '4019283',
      mcNumber: '1247583',
      fleet: { trucks: 1, trailers: 1, drivers: 1 },
      insurance: { expiry: '2026-09-10', provider: 'Great West Casualty', coverage: '$1,000,000' },
      joined: '2026-01-10',
      stats: { totalLoads: 45, onTimeDelivery: 98, avgRating: 4.9, totalRevenue: 98500, avgRatePerMile: 2.52 },
      loadHistory: [
        { id: 'BRZ-003', route: 'Los Angeles, CA → Denver, CO', date: '2026-03-15', rate: 2400, status: 'delivered' }
      ],
      notes: 'Excellent reliability. Perfect for temperature-controlled loads. Very professional.'
    },
    'Eagle Transport Inc': {
      id: 'CLT-004',
      company: 'Eagle Transport Inc',
      package: 'Silver',
      contact: { name: 'Robert Martinez', title: 'Fleet Manager', email: 'robert@eagletransport.com', phone: '(206) 555-1234' },
      address: '2345 Pacific Way, Seattle, WA 98101',
      dotNumber: '2847291',
      mcNumber: '982734',
      fleet: { trucks: 3, trailers: 4, drivers: 4 },
      insurance: { expiry: '2026-07-25', provider: 'Sentry Insurance', coverage: '$2,000,000' },
      joined: '2025-08-20',
      stats: { totalLoads: 234, onTimeDelivery: 96, avgRating: 4.8, totalRevenue: 567000, avgRatePerMile: 2.78 },
      loadHistory: [
        { id: 'SLV-001', route: 'Seattle, WA → Salt Lake City, UT', date: '2026-03-17', rate: 2950, status: 'in_transit' },
        { id: 'SLV-018', route: 'Portland, OR → Phoenix, AZ', date: '2026-03-14', rate: 3100, status: 'delivered' },
        { id: 'SLV-012', route: 'San Francisco, CA → Denver, CO', date: '2026-03-10', rate: 2750, status: 'delivered' }
      ],
      notes: 'Growing carrier with excellent track record. Looking to expand fleet this year.'
    },
    'Swift Carriers': {
      id: 'CLT-005',
      company: 'Swift Carriers',
      package: 'Silver',
      contact: { name: 'Amanda Lee', title: 'Operations Manager', email: 'amanda@swiftcarriers.com', phone: '(503) 555-5678' },
      address: '789 Commerce St, Portland, OR 97201',
      dotNumber: '3182736',
      mcNumber: '1047382',
      fleet: { trucks: 2, trailers: 3, drivers: 3 },
      insurance: { expiry: '2026-11-30', provider: 'Progressive Commercial', coverage: '$1,500,000' },
      joined: '2025-09-15',
      stats: { totalLoads: 178, onTimeDelivery: 93, avgRating: 4.6, totalRevenue: 423000, avgRatePerMile: 2.65 },
      loadHistory: [
        { id: 'SLV-002', route: 'Portland, OR → San Francisco, CA', date: '2026-03-18', rate: 1850, status: 'in_transit' }
      ],
      notes: 'Reliable carrier for Pacific Northwest routes. Good communication.'
    },
    'Atlas Logistics': {
      id: 'CLT-006',
      company: 'Atlas Logistics',
      package: 'Gold',
      contact: { name: 'Michael Rodriguez', title: 'CEO', email: 'michael@atlaslogistics.com', phone: '(310) 555-9012' },
      address: '1000 Logistics Pkwy, Los Angeles, CA 90001',
      dotNumber: '1928374',
      mcNumber: '847293',
      fleet: { trucks: 8, trailers: 12, drivers: 10 },
      insurance: { expiry: '2026-12-15', provider: 'Zurich Insurance', coverage: '$5,000,000' },
      joined: '2025-03-01',
      stats: { totalLoads: 892, onTimeDelivery: 98, avgRating: 4.9, totalRevenue: 2450000, avgRatePerMile: 3.12 },
      loadHistory: [
        { id: 'GLD-001', route: 'Los Angeles, CA → Chicago, IL', date: '2026-03-17', rate: 5800, status: 'in_transit' },
        { id: 'GLD-002', route: 'New York, NY → Miami, FL', date: '2026-03-18', rate: 4200, status: 'in_transit' },
        { id: 'GLD-007', route: 'Atlanta, GA → Dallas, TX', date: '2026-03-22', rate: 2650, status: 'pending' }
      ],
      notes: 'Premium carrier. Dedicated account manager assigned. Handles high-value freight.'
    },
    'Titan Carriers': {
      id: 'CLT-007',
      company: 'Titan Carriers',
      package: 'Gold',
      contact: { name: 'Patricia Nguyen', title: 'VP Operations', email: 'patricia@titancarriers.com', phone: '(832) 555-3456' },
      address: '5678 Industrial Dr, Houston, TX 77002',
      dotNumber: '2019283',
      mcNumber: '912847',
      fleet: { trucks: 6, trailers: 8, drivers: 7 },
      insurance: { expiry: '2026-10-20', provider: 'Great West Casualty', coverage: '$3,000,000' },
      joined: '2025-05-10',
      stats: { totalLoads: 567, onTimeDelivery: 97, avgRating: 4.8, totalRevenue: 1780000, avgRatePerMile: 3.05 },
      loadHistory: [
        { id: 'GLD-003', route: 'Houston, TX → Los Angeles, CA', date: '2026-03-21', rate: 4650, status: 'pending' },
        { id: 'GLD-004', route: 'Dallas, TX → Memphis, TN', date: '2026-03-14', rate: 1850, status: 'delivered' }
      ],
      notes: 'Excellent for industrial equipment. Growing rapidly. Wants more dedicated lanes.'
    },
    'Mountain Express': {
      id: 'CLT-008',
      company: 'Mountain Express',
      package: 'Gold',
      contact: { name: 'Emily Jackson', title: 'Owner', email: 'emily@mountainexpress.com', phone: '(720) 555-7890' },
      address: '3456 Rocky Mountain Blvd, Denver, CO 80202',
      dotNumber: '3847261',
      mcNumber: '1129384',
      fleet: { trucks: 5, trailers: 6, drivers: 6 },
      insurance: { expiry: '2026-08-05', provider: 'Northland Insurance', coverage: '$2,500,000' },
      joined: '2025-06-20',
      stats: { totalLoads: 423, onTimeDelivery: 95, avgRating: 4.7, totalRevenue: 1250000, avgRatePerMile: 2.95 },
      loadHistory: [
        { id: 'GLD-005', route: 'Denver, CO → Kansas City, MO', date: '2026-03-19', rate: 1680, status: 'in_transit' },
        { id: 'GLD-006', route: 'Phoenix, AZ → Las Vegas, NV', date: '2026-03-15', rate: 1100, status: 'delivered' }
      ],
      notes: 'Specializes in mountain region routes. Excellent winter weather capabilities.'
    },
    'Quick Haul Inc': {
      id: 'CLT-009',
      company: 'Quick Haul Inc',
      package: 'Bronze',
      contact: { name: 'Tom Davis', title: 'Owner', email: 'tom@quickhaul.com', phone: '(312) 555-2345' },
      address: '234 Freight Lane, Chicago, IL 60601',
      dotNumber: '4128374',
      mcNumber: '1348271',
      fleet: { trucks: 1, trailers: 1, drivers: 1 },
      insurance: { expiry: '2026-05-15', provider: 'Progressive Commercial', coverage: '$1,000,000' },
      joined: '2026-02-01',
      stats: { totalLoads: 28, onTimeDelivery: 92, avgRating: 4.4, totalRevenue: 62000, avgRatePerMile: 2.48 },
      loadHistory: [
        { id: 'BRZ-004', route: 'Chicago, IL → Nashville, TN', date: '2026-03-19', rate: 1650, status: 'in_transit' }
      ],
      notes: 'New carrier. Showing good potential. Prefers Midwest routes.'
    },
    'Road Star Transport': {
      id: 'CLT-010',
      company: 'Road Star Transport',
      package: 'Bronze',
      contact: { name: 'Lisa Brown', title: 'Owner/Operator', email: 'lisa@roadstar.com', phone: '(305) 555-6789' },
      address: '567 Ocean Dr, Miami, FL 33101',
      dotNumber: '4219384',
      mcNumber: '1392847',
      fleet: { trucks: 1, trailers: 2, drivers: 1 },
      insurance: { expiry: '2026-07-30', provider: 'Sentry Insurance', coverage: '$1,000,000' },
      joined: '2025-10-15',
      stats: { totalLoads: 56, onTimeDelivery: 89, avgRating: 4.3, totalRevenue: 118000, avgRatePerMile: 2.35 },
      loadHistory: [
        { id: 'BRZ-005', route: 'Miami, FL → Charlotte, NC', date: '2026-03-22', rate: 2100, status: 'pending' }
      ],
      notes: 'Florida specialist. Good for Southeast regional loads.'
    },
    'Prime Haul LLC': {
      id: 'CLT-011',
      company: 'Prime Haul LLC',
      package: 'Silver',
      contact: { name: 'Kevin Park', title: 'Operations Director', email: 'kevin@primehaul.com', phone: '(612) 555-4321' },
      address: '890 Commerce Blvd, Minneapolis, MN 55401',
      dotNumber: '3281947',
      mcNumber: '1083746',
      fleet: { trucks: 2, trailers: 3, drivers: 2 },
      insurance: { expiry: '2026-09-25', provider: 'Great West Casualty', coverage: '$1,500,000' },
      joined: '2025-07-10',
      stats: { totalLoads: 145, onTimeDelivery: 94, avgRating: 4.6, totalRevenue: 345000, avgRatePerMile: 2.72 },
      loadHistory: [
        { id: 'SLV-003', route: 'Minneapolis, MN → Kansas City, MO', date: '2026-03-21', rate: 1420, status: 'pending' }
      ],
      notes: 'Reliable for medical supplies. Temperature-controlled equipment available.'
    },
    'Midwest Express': {
      id: 'CLT-012',
      company: 'Midwest Express',
      package: 'Silver',
      contact: { name: 'Jennifer White', title: 'Fleet Manager', email: 'jennifer@midwestexpress.com', phone: '(313) 555-8765' },
      address: '123 Motor City Rd, Detroit, MI 48201',
      dotNumber: '2918374',
      mcNumber: '973846',
      fleet: { trucks: 2, trailers: 2, drivers: 3 },
      insurance: { expiry: '2026-06-10', provider: 'Zurich Insurance', coverage: '$1,500,000' },
      joined: '2025-08-05',
      stats: { totalLoads: 189, onTimeDelivery: 96, avgRating: 4.7, totalRevenue: 412000, avgRatePerMile: 2.81 },
      loadHistory: [
        { id: 'SLV-004', route: 'Detroit, MI → Columbus, OH', date: '2026-03-14', rate: 980, status: 'delivered' }
      ],
      notes: 'Auto parts specialist. Strong relationships with OEM suppliers.'
    },
    'Heartland Trucking': {
      id: 'CLT-013',
      company: 'Heartland Trucking',
      package: 'Silver',
      contact: { name: 'Chris Taylor', title: 'Owner', email: 'chris@heartlandtrucking.com', phone: '(314) 555-2109' },
      address: '456 Gateway Arch Dr, St. Louis, MO 63101',
      dotNumber: '3019284',
      mcNumber: '1019283',
      fleet: { trucks: 2, trailers: 3, drivers: 2 },
      insurance: { expiry: '2026-11-15', provider: 'Progressive Commercial', coverage: '$1,500,000' },
      joined: '2025-09-01',
      stats: { totalLoads: 156, onTimeDelivery: 95, avgRating: 4.8, totalRevenue: 378000, avgRatePerMile: 2.85 },
      loadHistory: [
        { id: 'SLV-005', route: 'St. Louis, MO → Indianapolis, IN', date: '2026-03-19', rate: 1100, status: 'in_transit' }
      ],
      notes: 'Pharmaceutical certified. Excellent for high-value medical shipments.'
    },
    'Blue Ridge Logistics': {
      id: 'CLT-014',
      company: 'Blue Ridge Logistics',
      package: 'Silver',
      contact: { name: 'Daniel Kim', title: 'Operations Manager', email: 'daniel@blueridgelogistics.com', phone: '(804) 555-6543' },
      address: '789 Colonial Way, Richmond, VA 23219',
      dotNumber: '3847162',
      mcNumber: '1128374',
      fleet: { trucks: 2, trailers: 2, drivers: 2 },
      insurance: { expiry: '2026-08-20', provider: 'Northland Insurance', coverage: '$1,500,000' },
      joined: '2025-10-20',
      stats: { totalLoads: 134, onTimeDelivery: 97, avgRating: 4.7, totalRevenue: 298000, avgRatePerMile: 2.75 },
      loadHistory: [
        { id: 'SLV-006', route: 'Richmond, VA → Philadelphia, PA', date: '2026-03-16', rate: 1250, status: 'delivered' }
      ],
      notes: 'East Coast specialist. Furniture and home goods expert.'
    }
  };

  // Package definitions for Grow (Dispatch Services)
  const packages = {
    bronze: {
      name: 'Bronze',
      subtitle: 'Dispatch Essentials',
      price: '$300',
      priceType: '/month',
      color: '#CD7F32',
      icon: Award,
      description: 'Basic dispatch support for owner-operators',
      features: [
        'Load board access',
        'Basic load matching',
        'Email support',
        'Weekly market updates',
        'Standard rate negotiation'
      ],
      stats: {
        activeClients: 34,
        totalLoads: 412,
        avgRatePerMile: 2.45,
        monthlyRevenue: 10200,
        avgLoadsPerClient: 12,
        clientRetention: 78
      },
      loads: [
        { id: 'BRZ-001', carrier: 'Solo Express LLC', driver: 'James Wilson', truck: '2022 Freightliner', status: 'in_transit', pickup: { city: 'Dallas, TX', date: '2026-03-18', time: '08:00 AM' }, delivery: { city: 'Atlanta, GA', date: '2026-03-20', time: '02:00 PM' }, rate: 3200, miles: 781, ratePerMile: 4.10, commodity: 'Electronics', weight: '42,000 lbs', progress: 65, currentLocation: { lat: 32.4, lng: -89.1, city: 'Jackson, MS' } },
        { id: 'BRZ-002', carrier: 'Highway Runner', driver: 'Mike Chen', truck: '2021 Peterbilt', status: 'pending', pickup: { city: 'Houston, TX', date: '2026-03-21', time: '06:00 AM' }, delivery: { city: 'Phoenix, AZ', date: '2026-03-23', time: '04:00 PM' }, rate: 2850, miles: 1178, ratePerMile: 2.42, commodity: 'Auto Parts', weight: '38,000 lbs', progress: 0, currentLocation: null },
        { id: 'BRZ-003', carrier: 'Mile High Trucking', driver: 'Sarah Johnson', truck: '2023 Kenworth', status: 'delivered', pickup: { city: 'Los Angeles, CA', date: '2026-03-15', time: '07:00 AM' }, delivery: { city: 'Denver, CO', date: '2026-03-17', time: '03:00 PM' }, rate: 2400, miles: 1020, ratePerMile: 2.35, commodity: 'Retail Goods', weight: '35,000 lbs', progress: 100, currentLocation: null },
        { id: 'BRZ-004', carrier: 'Quick Haul Inc', driver: 'Tom Davis', truck: '2020 Volvo', status: 'in_transit', pickup: { city: 'Chicago, IL', date: '2026-03-19', time: '05:00 AM' }, delivery: { city: 'Nashville, TN', date: '2026-03-20', time: '06:00 PM' }, rate: 1650, miles: 473, ratePerMile: 3.49, commodity: 'Food Products', weight: '40,000 lbs', progress: 45, currentLocation: { lat: 37.8, lng: -87.5, city: 'Evansville, IN' } },
        { id: 'BRZ-005', carrier: 'Road Star Transport', driver: 'Lisa Brown', truck: '2022 Mack', status: 'pending', pickup: { city: 'Miami, FL', date: '2026-03-22', time: '04:00 AM' }, delivery: { city: 'Charlotte, NC', date: '2026-03-24', time: '12:00 PM' }, rate: 2100, miles: 651, ratePerMile: 3.23, commodity: 'Construction Materials', weight: '44,000 lbs', progress: 0, currentLocation: null }
      ]
    },
    silver: {
      name: 'Silver',
      subtitle: 'Growth Dispatch',
      price: '$400',
      priceType: '/month',
      color: '#C0C0C0',
      icon: Star,
      description: 'Enhanced dispatch for growing carriers',
      features: [
        'Everything in Bronze',
        'Dedicated dispatcher',
        'Priority load matching',
        'Phone & email support',
        'Bi-weekly strategy calls',
        'Lane optimization',
        'Fuel card program access'
      ],
      stats: {
        activeClients: 28,
        totalLoads: 672,
        avgRatePerMile: 2.78,
        monthlyRevenue: 11200,
        avgLoadsPerClient: 24,
        clientRetention: 89
      },
      loads: [
        { id: 'SLV-001', carrier: 'Eagle Transport Inc', driver: 'Robert Martinez', truck: '2023 Freightliner Cascadia', status: 'in_transit', pickup: { city: 'Seattle, WA', date: '2026-03-17', time: '06:00 AM' }, delivery: { city: 'Salt Lake City, UT', date: '2026-03-19', time: '02:00 PM' }, rate: 2950, miles: 839, ratePerMile: 3.52, commodity: 'Tech Equipment', weight: '36,000 lbs', progress: 78, currentLocation: { lat: 43.6, lng: -116.2, city: 'Boise, ID' } },
        { id: 'SLV-002', carrier: 'Swift Carriers', driver: 'Amanda Lee', truck: '2022 Peterbilt 579', status: 'in_transit', pickup: { city: 'Portland, OR', date: '2026-03-18', time: '07:00 AM' }, delivery: { city: 'San Francisco, CA', date: '2026-03-19', time: '05:00 PM' }, rate: 1850, miles: 636, ratePerMile: 2.91, commodity: 'Wine & Beverages', weight: '32,000 lbs', progress: 55, currentLocation: { lat: 40.5, lng: -122.4, city: 'Redding, CA' } },
        { id: 'SLV-003', carrier: 'Prime Haul LLC', driver: 'Kevin Park', truck: '2023 Kenworth T680', status: 'pending', pickup: { city: 'Minneapolis, MN', date: '2026-03-21', time: '05:00 AM' }, delivery: { city: 'Kansas City, MO', date: '2026-03-22', time: '03:00 PM' }, rate: 1420, miles: 438, ratePerMile: 3.24, commodity: 'Medical Supplies', weight: '28,000 lbs', progress: 0, currentLocation: null },
        { id: 'SLV-004', carrier: 'Midwest Express', driver: 'Jennifer White', truck: '2021 Volvo VNL', status: 'delivered', pickup: { city: 'Detroit, MI', date: '2026-03-14', time: '08:00 AM' }, delivery: { city: 'Columbus, OH', date: '2026-03-15', time: '01:00 PM' }, rate: 980, miles: 263, ratePerMile: 3.73, commodity: 'Auto Parts', weight: '41,000 lbs', progress: 100, currentLocation: null },
        { id: 'SLV-005', carrier: 'Heartland Trucking', driver: 'Chris Taylor', truck: '2022 International LT', status: 'in_transit', pickup: { city: 'St. Louis, MO', date: '2026-03-19', time: '04:00 AM' }, delivery: { city: 'Indianapolis, IN', date: '2026-03-20', time: '10:00 AM' }, rate: 1100, miles: 243, ratePerMile: 4.53, commodity: 'Pharmaceuticals', weight: '25,000 lbs', progress: 30, currentLocation: { lat: 39.2, lng: -89.6, city: 'Springfield, IL' } },
        { id: 'SLV-006', carrier: 'Blue Ridge Logistics', driver: 'Daniel Kim', truck: '2023 Mack Anthem', status: 'delivered', pickup: { city: 'Richmond, VA', date: '2026-03-16', time: '06:00 AM' }, delivery: { city: 'Philadelphia, PA', date: '2026-03-17', time: '11:00 AM' }, rate: 1250, miles: 298, ratePerMile: 4.19, commodity: 'Furniture', weight: '30,000 lbs', progress: 100, currentLocation: null }
      ]
    },
    gold: {
      name: 'Gold',
      subtitle: 'Premium Fleet Dispatch',
      price: '$500',
      priceType: '/month',
      color: '#D4AF37',
      icon: Crown,
      popular: true,
      description: 'Full-service dispatch for established fleets',
      features: [
        'Everything in Silver',
        'Dedicated account manager',
        '24/7 dispatch support',
        'Weekly strategy calls',
        'Advanced lane analytics',
        'Broker relationship management',
        'Fleet expansion consulting',
        'Custom reporting dashboard',
        'Priority rate negotiation'
      ],
      stats: {
        activeClients: 15,
        totalLoads: 890,
        avgRatePerMile: 3.12,
        monthlyRevenue: 7500,
        avgLoadsPerClient: 59,
        clientRetention: 96
      },
      loads: [
        { id: 'GLD-001', carrier: 'Atlas Logistics', driver: 'Michael Rodriguez', truck: '2024 Freightliner Cascadia', status: 'in_transit', pickup: { city: 'Los Angeles, CA', date: '2026-03-17', time: '05:00 AM' }, delivery: { city: 'Chicago, IL', date: '2026-03-20', time: '08:00 AM' }, rate: 5800, miles: 2015, ratePerMile: 2.88, commodity: 'High-Value Electronics', weight: '38,000 lbs', progress: 42, currentLocation: { lat: 35.2, lng: -111.6, city: 'Flagstaff, AZ' } },
        { id: 'GLD-002', carrier: 'Atlas Logistics', driver: 'Steven Chang', truck: '2024 Peterbilt 579', status: 'in_transit', pickup: { city: 'New York, NY', date: '2026-03-18', time: '04:00 AM' }, delivery: { city: 'Miami, FL', date: '2026-03-21', time: '06:00 PM' }, rate: 4200, miles: 1280, ratePerMile: 3.28, commodity: 'Retail Merchandise', weight: '42,000 lbs', progress: 68, currentLocation: { lat: 31.2, lng: -81.5, city: 'Brunswick, GA' } },
        { id: 'GLD-003', carrier: 'Titan Carriers', driver: 'Patricia Nguyen', truck: '2023 Kenworth W990', status: 'pending', pickup: { city: 'Houston, TX', date: '2026-03-21', time: '06:00 AM' }, delivery: { city: 'Los Angeles, CA', date: '2026-03-24', time: '04:00 PM' }, rate: 4650, miles: 1547, ratePerMile: 3.01, commodity: 'Industrial Equipment', weight: '44,000 lbs', progress: 0, currentLocation: null },
        { id: 'GLD-004', carrier: 'Titan Carriers', driver: 'William Thompson', truck: '2024 Volvo VNL 860', status: 'delivered', pickup: { city: 'Dallas, TX', date: '2026-03-14', time: '07:00 AM' }, delivery: { city: 'Memphis, TN', date: '2026-03-15', time: '05:00 PM' }, rate: 1850, miles: 453, ratePerMile: 4.08, commodity: 'Consumer Goods', weight: '36,000 lbs', progress: 100, currentLocation: null },
        { id: 'GLD-005', carrier: 'Mountain Express', driver: 'Emily Jackson', truck: '2023 International LT', status: 'in_transit', pickup: { city: 'Denver, CO', date: '2026-03-19', time: '05:00 AM' }, delivery: { city: 'Kansas City, MO', date: '2026-03-20', time: '04:00 PM' }, rate: 1680, miles: 604, ratePerMile: 2.78, commodity: 'Agricultural Products', weight: '40,000 lbs', progress: 55, currentLocation: { lat: 39.1, lng: -101.3, city: 'Colby, KS' } },
        { id: 'GLD-006', carrier: 'Mountain Express', driver: 'David Williams', truck: '2024 Mack Anthem', status: 'delivered', pickup: { city: 'Phoenix, AZ', date: '2026-03-15', time: '06:00 AM' }, delivery: { city: 'Las Vegas, NV', date: '2026-03-16', time: '10:00 AM' }, rate: 1100, miles: 297, ratePerMile: 3.70, commodity: 'Event Equipment', weight: '28,000 lbs', progress: 100, currentLocation: null },
        { id: 'GLD-007', carrier: 'Atlas Logistics', driver: 'Jessica Brown', truck: '2024 Freightliner', status: 'pending', pickup: { city: 'Atlanta, GA', date: '2026-03-22', time: '05:00 AM' }, delivery: { city: 'Dallas, TX', date: '2026-03-24', time: '02:00 PM' }, rate: 2650, miles: 781, ratePerMile: 3.39, commodity: 'Machinery Parts', weight: '39,000 lbs', progress: 0, currentLocation: null }
      ]
    }
  };

  // Overall stats across all packages
  const overallStats = {
    totalClients: 77,
    totalLoads: 1974,
    totalRevenue: 28900,
    avgRetention: 88
  };

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    setTimeout(() => setLoading(false), 500);
  }, [navigate]);

  const getStatusCounts = (loads) => ({
    pending: loads.filter(l => l.status === 'pending').length,
    inTransit: loads.filter(l => l.status === 'in_transit').length,
    delivered: loads.filter(l => l.status === 'delivered').length
  });

  const handleCarrierClick = (carrierName) => {
    const profile = clientProfiles[carrierName];
    if (profile) {
      setSelectedClient(profile);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Grow" subtitle="Loading...">
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  // Client Profile Modal
  if (selectedClient) {
    return (
      <AdminLayout title="Client Profile" subtitle={selectedClient.company}>
        <button
          onClick={() => setSelectedClient(null)}
          className="flex items-center gap-2 text-white/50 hover:text-white mb-6 transition-colors"
          data-testid="back-to-loadboard-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Load Board
        </button>

        {/* Using reusable ClientProfileModal component */}
        <ClientProfileModal client={selectedClient} onClose={() => setSelectedClient(null)} isModal={false} />
      </AdminLayout>
    );
  }

  // Package Selection Screen
  if (!selectedPackage) {
    return (
      <AdminLayout title="Grow Center" subtitle="Dispatch services - Select a package to view load board">
        <div className="bg-gradient-to-r from-[#D4AF37]/20 to-[#D4AF37]/5 border border-[#D4AF37]/30 p-4 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-white text-lg font-bold mb-1">Dispatch Services Overview</h2>
              <p className="text-white/50 text-sm">Managing {overallStats.totalClients} clients across all packages</p>
            </div>
            <div className="flex items-center gap-4 sm:gap-6">
              <div className="text-center">
                <p className="text-2xl sm:text-3xl font-bold text-[#10B981]">{overallStats.totalLoads}</p>
                <p className="text-white/40 text-xs">Total Loads</p>
              </div>
              <div className="h-10 w-px bg-white/10" />
              <div className="text-center">
                <p className="text-2xl sm:text-3xl font-bold text-[#D4AF37]">${(overallStats.totalRevenue / 1000).toFixed(1)}k</p>
                <p className="text-white/40 text-xs">Monthly Revenue</p>
              </div>
              <div className="h-10 w-px bg-white/10" />
              <div className="text-center">
                <p className="text-2xl sm:text-3xl font-bold text-white">{overallStats.avgRetention}%</p>
                <p className="text-white/40 text-xs">Retention</p>
              </div>
            </div>
          </div>
        </div>

        <h3 className="text-white font-bold mb-4">Select a Package to View Load Board</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.entries(packages).map(([key, pkg]) => {
            const statusCounts = getStatusCounts(pkg.loads);
            return (
              <div
                key={key}
                onClick={() => setSelectedPackage(key)}
                className={`bg-[#121212] border p-6 cursor-pointer hover:bg-[#1a1a1a] transition-all relative ${
                  pkg.popular ? 'border-[#D4AF37]' : 'border-white/10 hover:border-white/20'
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-[#D4AF37] text-black text-xs font-bold px-3 py-1">MOST POPULAR</span>
                  </div>
                )}
                
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 rounded" style={{ backgroundColor: `${pkg.color}20` }}>
                    <pkg.icon className="w-6 h-6" style={{ color: pkg.color }} />
                  </div>
                  <div>
                    <p className="text-white/50 text-xs uppercase tracking-wider">{pkg.name}</p>
                    <p className="text-white font-bold">{pkg.subtitle}</p>
                  </div>
                </div>

                <div className="mb-4">
                  <span className="text-3xl font-bold" style={{ color: pkg.color }}>{pkg.price}</span>
                  <span className="text-white/40 text-sm">{pkg.priceType}</span>
                </div>

                <div className="grid grid-cols-3 gap-2 mb-4 p-3 bg-[#0A0A0A] border border-white/5">
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Circle className="w-2 h-2 fill-yellow-500 text-yellow-500" />
                      <span className="text-yellow-500 font-bold">{statusCounts.pending}</span>
                    </div>
                    <p className="text-white/40 text-[10px]">Pending</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Circle className="w-2 h-2 fill-blue-500 text-blue-500" />
                      <span className="text-blue-500 font-bold">{statusCounts.inTransit}</span>
                    </div>
                    <p className="text-white/40 text-[10px]">In Transit</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Circle className="w-2 h-2 fill-green-500 text-green-500" />
                      <span className="text-green-500 font-bold">{statusCounts.delivered}</span>
                    </div>
                    <p className="text-white/40 text-[10px]">Delivered</p>
                  </div>
                </div>

                <div className="border-t border-white/10 pt-4">
                  <div className="grid grid-cols-2 gap-4 mb-2">
                    <div>
                      <p className="text-white/40 text-xs">Active Clients</p>
                      <p className="text-white font-bold text-xl">{pkg.stats.activeClients}</p>
                    </div>
                    <div>
                      <p className="text-white/40 text-xs">Total Loads</p>
                      <p className="text-white font-bold text-xl">{pkg.stats.totalLoads}</p>
                    </div>
                  </div>
                </div>

                <button 
                  className="w-full mt-4 py-3 text-sm font-medium transition-all"
                  style={{ backgroundColor: `${pkg.color}20`, color: pkg.color, border: `1px solid ${pkg.color}40` }}
                >
                  View Load Board →
                </button>
              </div>
            );
          })}
        </div>
      </AdminLayout>
    );
  }

  // Package-Specific Load Board
  const pkg = packages[selectedPackage];
  const statusCounts = getStatusCounts(pkg.loads);
  const inTransitLoads = pkg.loads.filter(l => l.status === 'in_transit');

  return (
    <AdminLayout title={`${pkg.name} Load Board`} subtitle={pkg.subtitle}>
      <button
        onClick={() => { setSelectedPackage(null); setSelectedLoad(null); }}
        className="flex items-center gap-2 text-white/50 hover:text-white mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to All Packages
      </button>

      {/* Package Header */}
      <div className="border p-4 sm:p-6 mb-6" style={{ backgroundColor: `${pkg.color}10`, borderColor: `${pkg.color}40` }}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded" style={{ backgroundColor: `${pkg.color}20` }}>
              <pkg.icon className="w-8 h-8" style={{ color: pkg.color }} />
            </div>
            <div>
              <h2 className="text-white text-xl font-bold">{pkg.name} - {pkg.subtitle}</h2>
              <p className="text-white/50">{pkg.price}{pkg.priceType} • {pkg.stats.activeClients} active clients</p>
            </div>
          </div>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Circle className="w-3 h-3 fill-yellow-500 text-yellow-500" />
              <span className="text-white font-bold">{statusCounts.pending}</span>
              <span className="text-white/40 text-sm">Pending</span>
            </div>
            <div className="flex items-center gap-2">
              <Circle className="w-3 h-3 fill-blue-500 text-blue-500" />
              <span className="text-white font-bold">{statusCounts.inTransit}</span>
              <span className="text-white/40 text-sm">In Transit</span>
            </div>
            <div className="flex items-center gap-2">
              <Circle className="w-3 h-3 fill-green-500 text-green-500" />
              <span className="text-white font-bold">{statusCounts.delivered}</span>
              <span className="text-white/40 text-sm">Delivered</span>
            </div>
          </div>
        </div>
      </div>

      {/* GPS Map */}
      <div className="bg-[#121212] border border-white/10 mb-6 overflow-hidden">
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Navigation className="w-5 h-5" style={{ color: pkg.color }} />
            <h3 className="text-white font-bold">Live GPS Tracking</h3>
          </div>
          <span className="text-white/50 text-sm">{inTransitLoads.length} trucks in transit</span>
        </div>
        <div className="relative h-64 sm:h-80 bg-[#0A0A0A]">
          <div className="absolute inset-0 opacity-30">
            <svg viewBox="0 0 960 600" className="w-full h-full">
              <path d="M158,453 L181,463 L185,450 L213,447 L216,454 L232,455 L242,464 L269,455 L290,468 L321,453 L350,448 L365,458 L393,461 L429,447 L458,456 L494,445 L529,457 L557,447 L584,461 L621,447 L652,462 L687,445 L716,454 L747,449 L782,463 L815,447 L837,454 L837,410 L821,398 L801,403 L789,389 L772,395 L749,384 L732,391 L716,379 L697,387 L679,374 L654,382 L632,371 L615,378 L593,364 L574,374 L551,361 L531,371 L511,357 L487,368 L467,354 L443,365 L421,351 L398,362 L374,347 L354,359 L329,344 L307,356 L285,341 L261,353 L241,338 L217,351 L195,335 L172,349 L151,332 L128,346 L107,329 L85,343 L65,325 L45,341 L25,320 L25,453 L158,453" fill="#1a1a1a" stroke="#333" strokeWidth="1"/>
            </svg>
          </div>
          
          {inTransitLoads.map((load) => {
            const xPos = load.currentLocation ? ((load.currentLocation.lng + 125) / 65) * 100 : 50;
            const yPos = load.currentLocation ? ((50 - load.currentLocation.lat) / 30) * 100 : 50;
            
            return (
              <div 
                key={load.id}
                className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer group z-10"
                style={{ left: `${Math.max(5, Math.min(95, xPos))}%`, top: `${Math.max(10, Math.min(90, yPos))}%` }}
                onClick={() => setSelectedLoad(load)}
              >
                <div className={`relative ${selectedLoad?.id === load.id ? 'scale-125' : ''} transition-transform`}>
                  <div className="w-10 h-10 rounded-full flex items-center justify-center animate-pulse" style={{ backgroundColor: `${pkg.color}40` }}>
                    <Truck className="w-5 h-5" style={{ color: pkg.color }} />
                  </div>
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-20">
                    <div className="bg-[#1a1a1a] border border-white/20 px-3 py-2 rounded shadow-lg whitespace-nowrap">
                      <p className="text-white text-sm font-bold">{load.carrier}</p>
                      <p className="text-white/50 text-xs">{load.currentLocation?.city}</p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          
          <div className="absolute bottom-4 left-4 bg-[#1a1a1a]/90 border border-white/10 p-3">
            <p className="text-white/50 text-xs mb-2">Active Routes</p>
            {inTransitLoads.slice(0, 3).map((load) => (
              <div key={load.id} className="flex items-center gap-2 text-xs mb-1">
                <Truck className="w-3 h-3" style={{ color: pkg.color }} />
                <span className="text-white/70">{load.pickup.city.split(',')[0]} → {load.delivery.city.split(',')[0]}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 mb-6 border-b border-white/10 pb-4 overflow-x-auto">
        {[
          { id: 'loadboard', label: 'Load Board', icon: Package },
          { id: 'pending', label: `Pending (${statusCounts.pending})`, icon: Clock },
          { id: 'intransit', label: `In Transit (${statusCounts.inTransit})`, icon: Truck },
          { id: 'delivered', label: `Delivered (${statusCounts.delivered})`, icon: CheckCircle },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
              activeTab === tab.id ? 'bg-[#D4AF37]/10 border border-[#D4AF37]/30' : 'text-white/50 hover:text-white border border-transparent'
            }`}
            style={{ color: activeTab === tab.id ? pkg.color : undefined }}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Load Board Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {activeTab === 'loadboard' && <LoadsList loads={pkg.loads} color={pkg.color} selectedLoad={selectedLoad} onSelectLoad={setSelectedLoad} onCarrierClick={handleCarrierClick} />}
          {activeTab === 'pending' && <LoadsList loads={pkg.loads.filter(l => l.status === 'pending')} color={pkg.color} selectedLoad={selectedLoad} onSelectLoad={setSelectedLoad} onCarrierClick={handleCarrierClick} />}
          {activeTab === 'intransit' && <LoadsList loads={pkg.loads.filter(l => l.status === 'in_transit')} color={pkg.color} selectedLoad={selectedLoad} onSelectLoad={setSelectedLoad} onCarrierClick={handleCarrierClick} />}
          {activeTab === 'delivered' && <LoadsList loads={pkg.loads.filter(l => l.status === 'delivered')} color={pkg.color} selectedLoad={selectedLoad} onSelectLoad={setSelectedLoad} onCarrierClick={handleCarrierClick} />}
        </div>

        <div className="lg:col-span-1">
          {selectedLoad ? (
            <LoadDetails load={selectedLoad} color={pkg.color} onCarrierClick={handleCarrierClick} />
          ) : (
            <div className="bg-[#121212] border border-white/10 p-6 text-center">
              <Truck className="w-12 h-12 text-white/20 mx-auto mb-3" />
              <p className="text-white/50">Select a load to view details</p>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

// Loads List Component
const LoadsList = ({ loads, color, selectedLoad, onSelectLoad, onCarrierClick }) => {
  const statusConfig = {
    pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Pending', icon: Clock },
    in_transit: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'In Transit', icon: Truck },
    delivered: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Delivered', icon: CheckCircle }
  };

  return (
    <div className="bg-[#121212] border border-white/10 overflow-hidden">
      <div className="p-4 border-b border-white/10">
        <h3 className="text-white font-bold">Loads ({loads.length})</h3>
      </div>
      <div className="divide-y divide-white/5 max-h-[600px] overflow-y-auto">
        {loads.map((load) => {
          const status = statusConfig[load.status];
          const StatusIcon = status.icon;
          return (
            <div 
              key={load.id}
              onClick={() => onSelectLoad(load)}
              className={`p-4 cursor-pointer transition-all ${selectedLoad?.id === load.id ? 'bg-white/10' : 'hover:bg-white/5'}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-white font-mono text-sm" style={{ color }}>{load.id}</span>
                  <span className={`px-2 py-0.5 text-xs flex items-center gap-1 ${status.bg} ${status.text}`}>
                    <StatusIcon className="w-3 h-3" />
                    {status.label}
                  </span>
                </div>
                <span className="text-[#22C55E] font-bold">${load.rate.toLocaleString()}</span>
              </div>
              
              <div className="flex items-center gap-2 mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-3 h-3 text-white/40" />
                    <span className="text-white text-sm">{load.pickup.city}</span>
                  </div>
                </div>
                <ArrowUpRight className="w-4 h-4 text-white/30 rotate-45" />
                <div className="flex-1 text-right">
                  <div className="flex items-center gap-2 justify-end">
                    <span className="text-white text-sm">{load.delivery.city}</span>
                    <MapPin className="w-3 h-3 text-white/40" />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between text-xs">
                <button 
                  onClick={(e) => { e.stopPropagation(); onCarrierClick(load.carrier); }}
                  className="text-white/50 hover:text-[#D4AF37] hover:underline transition-colors flex items-center gap-1"
                >
                  <Building className="w-3 h-3" />
                  {load.carrier}
                </button>
                <span className="text-white/40">{load.miles} mi • ${load.ratePerMile.toFixed(2)}/mi</span>
              </div>

              {load.status === 'in_transit' && (
                <div className="mt-2">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-white/40">Progress</span>
                    <span className="text-white/60">{load.progress}%</span>
                  </div>
                  <div className="h-1.5 bg-white/10 overflow-hidden">
                    <div className="h-full transition-all" style={{ width: `${load.progress}%`, backgroundColor: color }} />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Load Details Component
const LoadDetails = ({ load, color, onCarrierClick }) => {
  const statusConfig = {
    pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Pending' },
    in_transit: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'In Transit' },
    delivered: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Delivered' }
  };
  const status = statusConfig[load.status];

  return (
    <div className="bg-[#121212] border border-white/10 overflow-hidden">
      <div className="p-4 border-b border-white/10" style={{ borderBottomColor: `${color}40` }}>
        <div className="flex items-center justify-between">
          <span className="text-lg font-bold" style={{ color }}>{load.id}</span>
          <span className={`px-2 py-1 text-xs ${status.bg} ${status.text}`}>{status.label}</span>
        </div>
      </div>

      <div className="p-4 space-y-4">
        <div>
          <p className="text-white/40 text-xs uppercase mb-1">Carrier</p>
          <button 
            onClick={() => onCarrierClick(load.carrier)}
            className="text-white font-medium hover:text-[#D4AF37] transition-colors flex items-center gap-2"
          >
            <Building className="w-4 h-4" />
            {load.carrier}
            <span className="text-xs text-white/40">→ View Profile</span>
          </button>
          <p className="text-white/50 text-sm">{load.driver} • {load.truck}</p>
        </div>

        <div className="bg-[#0A0A0A] border border-white/5 p-3">
          <div className="flex items-start gap-3">
            <div className="flex flex-col items-center">
              <div className="w-3 h-3 rounded-full bg-blue-500" />
              <div className="w-0.5 h-12 bg-white/20" />
              <div className="w-3 h-3 rounded-full bg-green-500" />
            </div>
            <div className="flex-1 space-y-4">
              <div>
                <p className="text-white font-medium">{load.pickup.city}</p>
                <p className="text-white/50 text-xs">{load.pickup.date} • {load.pickup.time}</p>
              </div>
              <div>
                <p className="text-white font-medium">{load.delivery.city}</p>
                <p className="text-white/50 text-xs">{load.delivery.date} • {load.delivery.time}</p>
              </div>
            </div>
          </div>
        </div>

        {load.status === 'in_transit' && load.currentLocation && (
          <div>
            <p className="text-white/40 text-xs uppercase mb-1">Current Location</p>
            <div className="flex items-center gap-2">
              <Navigation className="w-4 h-4" style={{ color }} />
              <span className="text-white">{load.currentLocation.city}</span>
            </div>
            <div className="mt-2">
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-white/40">Trip Progress</span>
                <span className="text-white/60">{load.progress}%</span>
              </div>
              <div className="h-2 bg-white/10 overflow-hidden">
                <div className="h-full transition-all" style={{ width: `${load.progress}%`, backgroundColor: color }} />
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div className="bg-[#0A0A0A] border border-white/5 p-3">
            <p className="text-white/40 text-xs">Rate</p>
            <p className="text-[#22C55E] font-bold text-lg">${load.rate.toLocaleString()}</p>
          </div>
          <div className="bg-[#0A0A0A] border border-white/5 p-3">
            <p className="text-white/40 text-xs">Rate/Mile</p>
            <p className="text-white font-bold text-lg">${load.ratePerMile.toFixed(2)}</p>
          </div>
          <div className="bg-[#0A0A0A] border border-white/5 p-3">
            <p className="text-white/40 text-xs">Miles</p>
            <p className="text-white font-bold text-lg">{load.miles.toLocaleString()}</p>
          </div>
          <div className="bg-[#0A0A0A] border border-white/5 p-3">
            <p className="text-white/40 text-xs">Weight</p>
            <p className="text-white font-bold text-lg">{load.weight}</p>
          </div>
        </div>

        <div>
          <p className="text-white/40 text-xs uppercase mb-1">Commodity</p>
          <p className="text-white">{load.commodity}</p>
        </div>
      </div>
    </div>
  );
};

export default AdminGrowSection;
